import $http from '@vue-util/http';
import { UserStatus } from '@vue-util/cookie';
import {
  RETURNS_BASE_PATH,
  UOP_RETURN_ITEMS_ENDPOINT,
  PREPARE_DECLARATION_ENDPOINT,
  RP_SUBMIT_RETURN_ENDPOINT,
  RP_SUBMIT_RETURN_ENDPOINT_V2,
  SHIPPING_DETAILS_ENDPOINT,
} from '#shared/endpoints';
import { createHeaders } from '@/utils/CreateHeaders';
import { mapDCRToRPV3Response } from '@/utils/returns/ConfirmationParser';

import { brand, RETURN_METHOD_KEY_MAP, RETURN_RESOLUTIONS } from 'config';
import { getBaseUrl } from '~/utils/General.js';

/* --------------------------------------------------------
 * Helpers
 * -------------------------------------------------------- */

function normalizeBase64Labels(rmaDetail = {}) {
  const base64Prefix = 'data:image/gif;base64,';
  rmaDetail.shippingLabels = rmaDetail.shippingLabels?.map((label) => {
    if (label.base64Label && !label.base64Label.startsWith(base64Prefix)) {
      label.base64Label = base64Prefix + label.base64Label;
    }
    return label;
  });
  return rmaDetail;
}

// Helper function to determine if RP(V1/V2) API should be used
function shouldUseRpApi({ returnItems, returnSelections, estimatedRefund, declarationId, returnMethods = [] }) {
  const isMarketPlaceOrder = returnItems?.isMkpOrder;
  const isExchangeEnabled = returnItems.isExchangeEnabled;
  const isReplacementEnabled = returnItems.isReplacementEnabled;

  /*
   * if RP UPS label free recipe is available and returnMethods also coming in prepare
   * along with declarationId and selected returnMethod is isRpEligible
   * then we can use RP_SUBMIT_RETURN_ENDPOINT_V2 for 1p/3p otherwise fallback to DCR
   */
  if (declarationId && returnMethods.length) {
    const selectedReturnMethod = returnMethods.find((method) => returnSelections.returnMethod === method.dropOffMethod);
    if (selectedReturnMethod?.isRpEligible) return RP_SUBMIT_RETURN_ENDPOINT_V2;
    return null;
  }

  // return method should be UPS
  if (returnSelections.returnMethod !== RETURN_METHOD_KEY_MAP.ups) return null;

  const containsPRItem = returnSelections.returnItems.every((i) => i.returnType === 'PR');

  // these conditions are only for 1P items
  const is1PConditionsMatched = !isMarketPlaceOrder && estimatedRefund.isWareHouseReturn && containsPRItem && declarationId;

  // conditions to call v2/submitDeclaration for 1P
  if ((isExchangeEnabled || isReplacementEnabled) && is1PConditionsMatched) return RP_SUBMIT_RETURN_ENDPOINT_V2;

  // conditions to call v2/submitDeclaration for 3P
  if (isMarketPlaceOrder && containsPRItem && returnItems?.isMkpV2RpReturnSubmitEnabled && declarationId) {
    return RP_SUBMIT_RETURN_ENDPOINT_V2;
  }

  // conditions to call v1/submitDeclaration only for 1P
  if (returnItems.isRpReturnSubmitEnabled && is1PConditionsMatched) return RP_SUBMIT_RETURN_ENDPOINT;

  return null;
}

/* --------------------------------------------------------
 * Payload Builders
 * -------------------------------------------------------- */

function createPrepareDeclarationPayload({ returnItems, returnSelections }) {
  const payload = {
    contact: {
      emailAddress: returnItems.shippingAddressInfo?.emailAddress,
      homePhone: returnItems.shippingAddressInfo?.homePhone,
    },
    orderNumber: returnItems.orderNumber,
    ...(UserStatus.isSignedIn() && {
      beneficiaryUserDetails: {
        firstName: returnItems.beneficiaryUserDetails?.firstName,
        lastName: returnItems.beneficiaryUserDetails?.lastName,
        dayPhone: returnItems.beneficiaryUserDetails?.dayPhone,
        emailAddress: returnItems.returnEmail,
      },
    }),
    cartonLines: returnSelections.returnItems.map((item) => ({
      fulfillmentChannel: item.fulfillmentChannel,
      fulfillmentType: item.fulfillmentType,
      fulfillmentStore: item.fulfillmentStore,
      returnQuantity: +item.quantity,
      returnType: item.returnType,
      upc: item.upc,
      returnReason: {
        code: +item.returnReason,
      },
    })),
  };

  return payload;
}

function createSubmitReturnPayload({
  returnItems,
  returnSelections,
  prepareDeclarationResponse,
  exchangeItem,
  useRpApi,
  applyConditionalReturnFee,
  isExchangeOrReplaceSelected,
}) {
  const payload = {
    contact: {
      emailAddress: returnItems?.shippingAddressInfo.emailAddress,
      homePhone: returnItems?.shippingAddressInfo.homePhone,
    },
    orderNumber: returnItems?.orderNumber,
    returnEmail: returnSelections.refundOption?.email || returnItems?.returnEmail,
    refundMethod: returnSelections.refundOption?.choice,
    returnDropOffMethod: returnSelections.returnMethod,
    ...(useRpApi && UserStatus.isSignedIn() && { beneficiaryUserDetails: returnItems.beneficiaryUserDetails, loyaltyUser: getLoyaltyStatus() }),
    ...(brand === 'mcom' && { applyConditionalReturnFee }),
    cartonLines: returnSelections.returnItems.map((item) => {
      const cartonLine = {
        ...(!returnItems.isMkpOrder && {
          fulfillmentStore: item.fulfillmentStore,
          fulfillmentChannel: item.fulfillmentChannel,
          fulfillmentType: item.fulfillmentType,
        }),
        upc: item.upc,
        returnQuantity: item.quantity,
        returnType: item.returnType,
        returnReason: {
          code: item.returnReason,
        },
      };
      if (item?.returnType !== 'PR') {
        cartonLine.mosFlag = item.mosFlag;
        cartonLine.virtualReturnExpectedBackDate = item.virtualReturnExpectedBackDate;
      }

      if (useRpApi && (returnItems.isExchangeEnabled || returnItems.isReplacementEnabled)) {
        let selectedUpcNumber;
        if (returnSelections.refundOption?.choice === RETURN_RESOLUTIONS.EXCHANGE) {
          selectedUpcNumber = exchangeItem.selected.upcNumber;
        } else if (returnSelections.refundOption?.choice === RETURN_RESOLUTIONS.REPLACEMENT) {
          selectedUpcNumber = prepareDeclarationResponse.cartonLines[0].upc;
        }
        const { shippingAddressDetails } = returnSelections;
        cartonLine.preferredResolution = {
          resolutionOptionCode: isExchangeOrReplaceSelected ? RETURN_RESOLUTIONS[returnSelections.refundOption?.choice] : 'REFUND',
          ...(!isExchangeOrReplaceSelected && { preferredRefundMethod: returnSelections.refundOption?.choice }),
          ...(isExchangeOrReplaceSelected && {
            outboundOrderLine: {
              upc: selectedUpcNumber,
              shippingAddress: {
                name: {
                  firstName: shippingAddressDetails.firstName,
                  lastName: shippingAddressDetails.lastName,
                  middleName: shippingAddressDetails.middleName,
                },
                address: {
                  addressLine1: shippingAddressDetails.addressLine1,
                  addressLine2: shippingAddressDetails.addressLine2,
                  city: shippingAddressDetails.city,
                  state: shippingAddressDetails.state,
                  zip: shippingAddressDetails.postalCode,
                  country: shippingAddressDetails.country,
                },
                email: returnItems?.returnEmail,
                phone: returnItems?.beneficiaryUserDetails?.dayPhone,
              },
            },
          }),
        };
      }

      return cartonLine;
    }),
  };

  if (useRpApi === RP_SUBMIT_RETURN_ENDPOINT_V2) {
    payload.rpPartnerId = prepareDeclarationResponse.rpPartnerId;
    payload.declarationId = prepareDeclarationResponse.declarationId;
  }
  return payload;
}

/* --------------------------------------------------------
 * API Calls (raw HTTP)
 * -------------------------------------------------------- */

async function fetchReturnItems({ token, ssrContext = {} }) {
  const { cookie, userAgent } = ssrContext;
  const headers = createHeaders({ ssrCookies: cookie, userAgent });
  const baseUrl = getBaseUrl();
  const isSignedIn = import.meta.server ? !!cookie?.match(/SignedIn=1/) : UserStatus.isSignedIn();

  const response = await $http.get(`${baseUrl}${UOP_RETURN_ITEMS_ENDPOINT}`, {
    ...headers,
    params: {
      token,
      verifyLookup: !isSignedIn ? 'true' : 'false',
    },
  });

  if (import.meta.server) {
    const { findAndDecode } = await import('@/utils/DecodeResponse');
    response.data = findAndDecode(response.data, ['orderToken', 'shipmentToken', 'pendingReturnId', 'pidToken']);
  }

  return response;
}

async function fetchShippingDetails({ pidToken }) {
  const headers = createHeaders();
  const response = await $http.get(SHIPPING_DETAILS_ENDPOINT, {
    ...headers,
    params: { pidToken },
  });
  const rmaDetail = response.data.rmaDetail;
  return normalizeBase64Labels(rmaDetail);
}

async function fetchShippingDetailsByRmaId({ rmaId }) {
  const headers = createHeaders();
  const response = await $http.get(`${RETURNS_BASE_PATH}/${rmaId}/getRMAShippingDetails`, {
    ...headers,
  });
  const rmaDetail = response.data.rmaDetail;
  return normalizeBase64Labels(rmaDetail);
}

async function submitPrepareDeclaration({ payload, verifyLookup }) {
  const headers = createHeaders();
  return await $http.post(PREPARE_DECLARATION_ENDPOINT, payload, {
    ...headers,
    params: { verifyLookup },
  });
}

async function submitReturn({ payload, endpoint }) {
  const headers = createHeaders();
  const response = await $http.post(endpoint, [payload], {
    ...headers,
    params: {
      verifyLookup: !UserStatus.isSignedIn() ? 'true' : 'false',
      _expand: 'productInfo,labelImage',
    },
  });
  // confirmation parser only use for dcr response, convert dcr response to rp response
  if (response.data.returnDetails?.isDcrRedesignEnabled) response.data = mapDCRToRPV3Response(response.data);
  return response;
}

export const returnsService = {
  shouldUseRpApi,
  createPrepareDeclarationPayload,
  createSubmitReturnPayload,

  fetchReturnItems,
  fetchShippingDetails,
  fetchShippingDetailsByRmaId,

  submitPrepareDeclaration,
  submitReturn,
};
